import { useState, useEffect, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { useMe } from '../../hooks/useMe'
import { C, F } from './theme'
import { Card, Btn, Icon, Head, LockedCard, gbp } from './prims'
import type { ForecastContract, ForecastContractBody } from '../../types'

// EM Sprint 1.5 #5 · Contracts — register of the suppliers/contractors
// underpinning the event: timeline, fees, advance payments, penalties, red
// flags. Fees feed the forecast P&L (server-side). Document upload + Claude
// legal review attach per-contract (ContractDocPanel). Admin + financial gated.

const inputStyle: CSSProperties = { width: '100%', padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 7, fontFamily: F.body, fontSize: 12.5, color: C.ink, boxSizing: 'border-box', background: C.card }
const FLAG: Record<string, { label: string; fg: string; bg: string }> = {
  none: { label: 'No flag', fg: C.inkSoft, bg: C.paper },
  watch: { label: 'Watch', fg: C.amber, bg: C.amberSoft },
  high: { label: 'High risk', fg: C.oxblood, bg: C.oxbloodSoft }
}

function toBody(c: ForecastContract): ForecastContractBody {
  return {
    companyName: c.companyName, companyType: c.companyType, startDate: c.startDate, endDate: c.endDate,
    timelineNote: c.timelineNote, feePence: c.feePence, advancePaymentPence: c.advancePaymentPence, advanceDue: c.advanceDue,
    penaltyNote: c.penaltyNote, penaltyPence: c.penaltyPence, redFlagNote: c.redFlagNote, redFlagSeverity: c.redFlagSeverity, sortOrder: 0
  }
}

export function ContractsTab({ eventId }: { eventId: number }) {
  const { fc, addContract } = useForecast(eventId)
  const { data: me } = useMe()
  const [adding, setAdding] = useState(false)

  if (!me?.hasFinancial) return <LockedCard label="Contracts need financial access" />
  if (!fc) return null

  const contracts = fc.contracts
  const flagged = contracts.filter((c) => c.redFlagSeverity !== 'none').length
  const totalAdvance = contracts.reduce((s, c) => s + (c.advancePaymentPence || 0), 0)

  const add = async () => {
    setAdding(true)
    try {
      await addContract({ companyName: 'New contract', companyType: 'supplier', startDate: null, endDate: null, timelineNote: null, feePence: 0, advancePaymentPence: 0, advanceDue: null, penaltyNote: null, penaltyPence: null, redFlagNote: null, redFlagSeverity: 'none', sortOrder: contracts.length })
    } finally { setAdding(false) }
  }

  return (
    <div>
      <Head eyebrow="Commercial · contracts" title="Contracts"
        sub="Suppliers and contractors underpinning the event. Contract fees feed the P&L; timelines, advance payments, penalties and red flags inform the business case."
        right={<Btn variant="primary" icon="plus" disabled={adding} onClick={add}>{adding ? 'Adding…' : 'Add contract'}</Btn>} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Contracts" value={String(contracts.length)} />
        <Stat label="Total fees" value={gbp(fc.contractFeesPence)} />
        <Stat label="Advance exposure" value={gbp(totalAdvance)} />
        <Stat label="Red flags" value={String(flagged)} tone={flagged > 0 ? 'warn' : undefined} />
      </div>

      {contracts.length === 0 && (
        <Card style={{ textAlign: 'center', padding: 30 }}>
          <Icon name="file" size={26} color={C.inkFaint} />
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 800, color: C.ink, marginTop: 8 }}>No contracts yet</div>
          <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, marginTop: 4 }}>Add the suppliers and contractors this event depends on.</div>
        </Card>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {contracts.map((c) => <ContractCard key={c.id} contract={c} eventId={eventId} />)}
      </div>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div style={{ padding: '9px 14px', borderRadius: 10, background: tone === 'warn' ? C.oxbloodSoft : C.card, border: `1px solid ${tone === 'warn' ? C.oxblood + '44' : C.lineSoft}`, minWidth: 120 }}>
      <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: tone === 'warn' ? C.oxblood : C.inkFaint }}>{label}</div>
      <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 800, color: tone === 'warn' ? C.oxblood : C.ink, marginTop: 2 }}>{value}</div>
    </div>
  )
}

function ContractCard({ contract, eventId }: { contract: ForecastContract; eventId: number }) {
  const { updateContract, deleteContract } = useForecast(eventId)
  const [c, setC] = useState<ForecastContract>(contract)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSaved = useRef<string>(JSON.stringify(toBody(contract)))
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const patch = (p: Partial<ForecastContract>) => {
    const next = { ...c, ...p }
    setC(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const body = JSON.stringify(toBody(next))
      if (body === lastSaved.current) return
      lastSaved.current = body
      void updateContract(contract.id, toBody(next)).catch(() => { /* swallow */ })
    }, 600)
  }

  const flag = FLAG[c.redFlagSeverity] ?? FLAG.none!

  return (
    <Card style={{ borderLeft: `4px solid ${c.redFlagSeverity === 'high' ? C.oxblood : c.redFlagSeverity === 'watch' ? C.amber : C.greenSoft}` }}>
      {/* header row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <input value={c.companyName} placeholder="Company name" onChange={(e) => patch({ companyName: e.target.value })}
          style={{ ...inputStyle, flex: 1, fontFamily: F.serif, fontSize: 15, fontWeight: 700 }} />
        <select value={c.companyType} onChange={(e) => patch({ companyType: e.target.value as ForecastContract['companyType'] })} style={{ ...inputStyle, width: 130 }}>
          <option value="supplier">Supplier</option>
          <option value="contractor">Contractor</option>
        </select>
        <button onClick={() => { if (confirm('Delete this contract?')) void deleteContract(contract.id) }} title="Delete contract"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.inkFaint, padding: 6, display: 'flex' }}><Icon name="x" size={17} /></button>
      </div>

      {/* fields grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
        <Field label="Start date"><input type="date" value={c.startDate ?? ''} onChange={(e) => patch({ startDate: e.target.value || null })} style={inputStyle} /></Field>
        <Field label="End date"><input type="date" value={c.endDate ?? ''} onChange={(e) => patch({ endDate: e.target.value || null })} style={inputStyle} /></Field>
        <Field label="Contract fee"><Pounds pence={c.feePence} onChange={(v) => patch({ feePence: v ?? 0 })} /></Field>
        <Field label="Advance payment"><Pounds pence={c.advancePaymentPence} onChange={(v) => patch({ advancePaymentPence: v ?? 0 })} /></Field>
        <Field label="Advance due"><input type="date" value={c.advanceDue ?? ''} onChange={(e) => patch({ advanceDue: e.target.value || null })} style={inputStyle} /></Field>
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Timeline / milestones"><input value={c.timelineNote ?? ''} placeholder="e.g. deposit on signing, balance 30 days before" onChange={(e) => patch({ timelineNote: e.target.value || null })} style={inputStyle} /></Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, marginTop: 12 }}>
        <Field label="Penalty terms"><input value={c.penaltyNote ?? ''} placeholder="e.g. cancellation / late-delivery penalty" onChange={(e) => patch({ penaltyNote: e.target.value || null })} style={inputStyle} /></Field>
        <Field label="Penalty amount"><Pounds pence={c.penaltyPence} onChange={(v) => patch({ penaltyPence: v })} nullable /></Field>
      </div>

      {/* red flag */}
      <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: flag.bg, border: `1px solid ${flag.fg}22` }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: c.redFlagSeverity === 'none' ? 0 : 10 }}>
          <Icon name="flag" size={15} color={flag.fg} />
          <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: flag.fg }}>Red flag</span>
          <select value={c.redFlagSeverity} onChange={(e) => patch({ redFlagSeverity: e.target.value as ForecastContract['redFlagSeverity'] })} style={{ ...inputStyle, width: 130, marginLeft: 'auto', background: C.card }}>
            <option value="none">No flag</option>
            <option value="watch">Watch</option>
            <option value="high">High risk</option>
          </select>
        </div>
        {c.redFlagSeverity !== 'none' && (
          <input value={c.redFlagNote ?? ''} placeholder="What's the concern?" onChange={(e) => patch({ redFlagNote: e.target.value || null })} style={{ ...inputStyle, background: C.card }} />
        )}
      </div>

      <ContractDocPanel contract={contract} eventId={eventId} />
    </Card>
  )
}

function ContractDocPanel({ contract, eventId }: { contract: ForecastContract; eventId: number }) {
  const { uploadContractDoc, analyseContract } = useForecast(eventId)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const onFile = async (file: File) => {
    setErr(null)
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { setErr('PDF files only.'); return }
    if (file.size > 10 * 1024 * 1024) { setErr('That PDF is over the 10 MB limit.'); return }
    setBusy('upload')
    try { await uploadContractDoc(contract.id, file.name, await fileToBase64(file)) } catch { setErr('Upload failed.') } finally { setBusy(null) }
  }
  const analyse = async () => { setErr(null); setBusy('analyse'); try { await analyseContract(contract.id) } catch { setErr('The review could not be completed.') } finally { setBusy(null) } }
  const pick = () => fileRef.current?.click()

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${C.line}` }}>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = '' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Icon name="file" size={15} color={C.brass} />
        <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.inkSoft }}>Contract document</span>
        {contract.hasDoc ? (
          <>
            <a href={`/api/forecast/contracts/${contract.id}/document`} target="_blank" rel="noopener noreferrer" style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.green }}>{contract.docFilename || 'View PDF'}</a>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Btn variant="ghost" small icon="refresh" disabled={!!busy} onClick={pick}>{busy === 'upload' ? 'Uploading…' : 'Replace'}</Btn>
              <Btn variant="soft" small icon="sparkle" disabled={!!busy} onClick={analyse}>{busy === 'analyse' ? 'Reviewing…' : contract.aiAnalysedAt ? 'Re-review' : 'Analyse with Claude'}</Btn>
            </div>
          </>
        ) : (
          <div style={{ marginLeft: 'auto' }}>
            <Btn variant="soft" small icon="download" disabled={!!busy} onClick={pick}>{busy === 'upload' ? 'Uploading…' : 'Upload PDF'}</Btn>
          </div>
        )}
      </div>
      {err && <div style={{ marginTop: 8, fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.oxblood }}>{err}</div>}
      {busy === 'analyse' && <div style={{ marginTop: 8, fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>Claude is reading the contract and checking it against the forecast…</div>}

      {contract.aiAnalysedAt && (
        <div style={{ marginTop: 12, background: C.paper, border: `1px solid ${C.lineSoft}`, borderRadius: 10, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="sparkle" size={14} color={C.green} />
            <span style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.green }}>Claude legal review</span>
          </div>
          {contract.aiSummary && <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.ink, marginBottom: 10, lineHeight: 1.5 }}>{contract.aiSummary}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {contract.aiConcerns.map((cc, i) => (
              <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <ConcernBadge severity={cc.severity} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.ink, lineHeight: 1.45 }}><strong>{cc.area}:</strong> {cc.concern}</div>
                  {cc.evidence && <div style={{ fontFamily: F.mono, fontSize: 11, color: C.inkFaint, marginTop: 2 }}>“{cc.evidence}”</div>}
                  {cc.recommendation && <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkSoft, marginTop: 2 }}>→ {cc.recommendation}</div>}
                </div>
              </div>
            ))}
            {contract.aiConcerns.length === 0 && <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft }}>No specific concerns raised.</div>}
          </div>
          <div style={{ fontFamily: F.mono, fontSize: 10, color: C.inkFaint, marginTop: 10 }}>AI-assisted review — not legal advice. Verify against the contract.</div>
        </div>
      )}
    </div>
  )
}

function ConcernBadge({ severity }: { severity: string }) {
  const m: Record<string, { fg: string; bg: string; label: string }> = {
    high: { fg: C.oxblood, bg: C.oxbloodSoft, label: 'High' },
    watch: { fg: C.amber, bg: C.amberSoft, label: 'Watch' },
    info: { fg: C.green, bg: C.greenBg, label: 'Info' }
  }
  const s = m[severity] ?? m.info!
  return <span style={{ flexShrink: 0, fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: s.fg, background: s.bg, borderRadius: 5, padding: '2px 6px', minWidth: 44, textAlign: 'center' }}>{s.label}</span>
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => { const res = String(r.result); const comma = res.indexOf(','); resolve(comma >= 0 ? res.slice(comma + 1) : res) }
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontFamily: F.body, fontSize: 10.5, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  )
}

function Pounds({ pence, onChange, nullable }: { pence: number | null; onChange: (v: number | null) => void; nullable?: boolean }) {
  const [text, setText] = useState(pence == null ? '' : (pence / 100).toString())
  useEffect(() => { setText(pence == null ? '' : (pence / 100).toString()) }, [pence])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ fontFamily: F.mono, fontSize: 12.5, color: C.inkFaint }}>£</span>
      <input inputMode="decimal" value={text} onChange={(e) => { const v = e.target.value; setText(v); const n = parseFloat(v); onChange(Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : (nullable ? null : 0)) }} style={{ ...inputStyle, fontFamily: F.mono, textAlign: 'right' }} />
    </div>
  )
}
